'use client';

import type React from 'react';
import styled, { css, keyframes } from 'styled-components';

const pulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.4); }
  70%  { box-shadow: 0 0 0 10px rgba(0, 255, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0); }
`;

const MiniContainer = styled.div<{ $playing: boolean }>`
  height: 52px;
  min-width: 52px;
  padding: 0 12px 0 14px;
  border-radius: 26px;
  background: var(--nav-bg, rgba(10, 10, 20, 0.96));
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border: 1px solid
    ${({ $playing }) => ($playing ? 'rgba(0, 255, 0, 0.55)' : 'var(--nav-border, rgba(255, 0, 255, 0.3))')};
  color: var(--text-primary, #fff);
  display: flex;
  align-items: center;
  gap: 10px;
  pointer-events: all;
  box-shadow: 0 4px 20px var(--card-shadow, rgba(0, 0, 0, 0.3));
  transition:
    border-color 0.3s ease,
    box-shadow 0.3s ease,
    background-color 0.3s ease,
    transform 0.2s ease;

  ${({ $playing }) =>
    $playing &&
    css`
      animation: ${pulse} 2.5s infinite;
    `}

  &:hover {
    border-color: #00ff00;
    box-shadow: 0 6px 28px rgba(0, 255, 0, 0.25);
  }
`;

const ExpandArea = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: inherit;
  font: inherit;
  outline: none;

  &:hover span {
    color: #00ff00;
  }
`;

const MiniSCLabel = styled.span`
  font-size: 0.65rem;
  color: #ff2d95;
  font-weight: 700;
  letter-spacing: 0.5px;
  transition: color 0.2s;
`;

const PlayPauseButton = styled.button<{ $playing: boolean }>`
  background: ${({ $playing }) => ($playing ? '#00ff00' : 'rgba(255, 45, 149, 0.15)')};
  border: 1px solid ${({ $playing }) => ($playing ? '#00ff00' : '#ff2d95')};
  border-radius: 50%;
  width: 34px;
  height: 34px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  color: ${({ $playing }) => ($playing ? '#000000' : '#ff2d95')};
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.15);
    background: ${({ $playing }) => ($playing ? '#00ff00' : '#ff2d95')};
    color: ${({ $playing }) => ($playing ? '#000000' : '#ffffff')};
    box-shadow: 0 0 12px ${({ $playing }) => ($playing ? 'rgba(0, 255, 0, 0.4)' : 'rgba(255, 45, 149, 0.4)')};
  }

  &:active {
    transform: scale(0.95);
  }
`;

const MiniTrackName = styled.span`
  font-size: 0.7rem;
  color: var(--text-secondary, rgba(255, 255, 255, 0.75));
  font-variant-caps: all-petite-caps;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 130px;
  transition: color 0.2s;

  @media (max-width: 400px) {
    display: none;
  }
`;

interface MiniButtonProps {
  isPlaying: boolean;
  isExpanded: boolean;
  displayTitle: string;
  onToggleExpand: () => void;
  onTogglePlay: () => void;
}

/**
 * Compact floating bottom-right player pill with dedicated play/pause button and title expand trigger.
 */
export const MiniButton: React.FC<MiniButtonProps> = ({
  isPlaying,
  isExpanded,
  displayTitle,
  onToggleExpand,
  onTogglePlay,
}) => {
  return (
    <MiniContainer
      $playing={isPlaying}
      role="group"
      aria-label="SoundCloud mini player"
      title={`${isPlaying ? 'Playing' : 'Paused'}: ${displayTitle}`}
    >
      <ExpandArea
        type="button"
        onClick={onToggleExpand}
        aria-label={isExpanded ? 'Collapse player' : 'Expand player'}
      >
        <MiniSCLabel>SC</MiniSCLabel>
      </ExpandArea>

      <PlayPauseButton
        type="button"
        $playing={isPlaying}
        onClick={(e) => {
          e.stopPropagation();
          onTogglePlay();
        }}
        aria-label={isPlaying ? 'Pause' : 'Play'}
        title={isPlaying ? 'Pause playback' : 'Start playback'}
      >
        {isPlaying ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="currentColor"
            style={{ marginLeft: 2 }}
          >
            <polygon points="5,3 19,12 5,21" />
          </svg>
        )}
      </PlayPauseButton>

      <ExpandArea
        type="button"
        onClick={onToggleExpand}
        aria-label={isExpanded ? 'Collapse player' : 'Expand player'}
      >
        <MiniTrackName>{displayTitle}</MiniTrackName>
      </ExpandArea>
    </MiniContainer>
  );
};

export default MiniButton;
